// @refresh reset
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { httpRequest } from '../config/api';
import {
  GET_CUSTOM_FIELDS,
  GET_EXTRACTION_STATS,
  GET_FUNDS_BY_PERIOD,
  GET_FUND_MAPPINGS,
  GET_OUTPUT_TABLES_STATS,
  GET_TRANSFORMATION_STATS
} from '../consts/queryKeys';
import authStore from '../store/auth';
import { USER_ROLES } from '../consts/userRoles';

export const serviceFundMapping = {
  getFundsByPeriodId: (periodId, params) =>
    httpRequest.get(`api/${periodId}/input-funds`, { ...params }),
  getFundMappings: params => httpRequest.get('api/fund-mappings', { params }),
  getAutoMatchFunds: sheetId => httpRequest.get(`api/sheets/auto-match-funds/${sheetId}`),
  getAutoMatchSheets: params => httpRequest.get(`api/automation/auto-match-sheets/`, { params }),
  createStatisticsJob: data => httpRequest.post('api/automation/statistics-jobs', data),
  getStatisticsJob: jobId => httpRequest.get(`api/automation/statistics-jobs/${jobId}`),
  getExtractionStats: periodId =>
    httpRequest.get(`api/automation/extraction-statistics/${periodId}`),
  getDetailedExtractionStats: (periodId, params) =>
    httpRequest.get(`api/automation/extraction-detailed-statistics/${periodId}`, {
      params
    }),
  getDetailedTransformationStats: (periodId, params) =>
    httpRequest.get(`api/automation/transformation-detailed-statistics/${periodId}`, {
      params
    }),
  getRefreshTransformationStats: (periodId, params) =>
    httpRequest.get(`api/automation/refresh-automation-transformations/${periodId}`, {
      params
    }),
  getDetailedOutputStats: (periodId, params) =>
    httpRequest.get(`api/automation/output-detailed-statistics/${periodId}`, {
      params
    }),
  getCustomFields: params => httpRequest.get('api/custom-fields/', { params }),
  downloadFile: params =>
    httpRequest.get(`api/periods/${params.periodId}/files/${params.fileId}`, {
      responseType: 'blob'
    }),
  createCustomField: data => httpRequest.post('api/custom-fields/', data),
  updateCustomField: ({ customFieldId, data }) =>
    httpRequest.put(`api/custom-fields/${customFieldId}`, data),
  deleteCustomField: id => httpRequest.delete(`api/custom-fields/${id}`),
  markAsPerInputFund: ({ sheetId, data }) =>
    httpRequest.post(`api/sheets/mark-sheet-as-per-input-fund/${sheetId}`, data),
  unmarkAsPerInputFund: ({ sheetId, data }) =>
    httpRequest.post(`api/sheets/unmark-sheet-as-per-input-fund/${sheetId}`, data),
  createFundMapping: data => httpRequest.post('api/fund-mappings', data),
  createFundMappingList: data => httpRequest.post('api/fund-mappings/save', data),
  createFund: ({ periodId, data }) => httpRequest.post(`api/${periodId}/input-funds`, data),
  updateFund: ({ fundId, data }) => httpRequest.put(`api/input-funds/${fundId}`, data),
  createSourceFund: data => httpRequest.post('api/source-funds', data),
  createFundsBulk: ({ periodId, workflowId, data }) =>
    httpRequest.post(`api/${periodId}/input-funds/bulk`, data, {
      headers: {
        workflow_id: workflowId
      }
    }),
  deleteFundsBulk: data => httpRequest.delete('api/input-funds-bulk/', data),
  updateSourceFund: ({ data, id }) => httpRequest.put(`api/source-funds/${id}`, data),
  deleteSourceFund: id => httpRequest.delete(`api/source-funds/${id}`),
  deleteFundMapping: id => httpRequest.delete(`api/fund-mappings/${id}`),
  deleteAllSourcesFund: sheetId => httpRequest.delete(`api/source-funds-for-sheet/${sheetId}`),
  clearAllSourcesFund: sheetId => httpRequest.delete(`api/fund-mappings-for-sheet/${sheetId}`),
  deleteFund: fundId => httpRequest.delete(`api/input-funds/${fundId}`),
  deleteFundFile: ({ fileId, periodId }) =>
    httpRequest.delete(`api/periods/${periodId}/files/${fileId}`),
  setPasswordToFile: ({ periodId, fileId, data }) =>
    httpRequest.post(`api/periods/${periodId}/files/set-password/${fileId}`, data),
  getFilesByPeriodId: periodId => httpRequest.get(`api/periods/${periodId}/files`),
  uploadFundFiles: ({ data, periodId, fundId, onUploadProgress }) =>
    httpRequest.post(
      `api/periods/${periodId}/files${fundId ? '?input_fund_id=' + fundId : ''}`,
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress
      }
    ),
  reUploadFundFile: ({ data, periodId, fileId, onUploadProgress }) =>
    httpRequest.put(`api/periods/${periodId}/files/${fileId}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress
    })
};

const useStatisticsJobQuery = ({ queryKey, periodId, params = {}, type, queryParams = {} }) => {
  const [jobId, setJobId] = useState(null);
  const [completedData, setCompletedData] = useState(undefined);
  const [jobError, setJobError] = useState(null);
  const paramsKey = useMemo(() => JSON.stringify(params || {}), [params]);
  // Reviewer accounts can't POST to api/automation/statistics-jobs — the
  // endpoint returns 403 for that role. Skip the query entirely instead of
  // firing a request the backend will always reject.
  const isApprover = authStore.role === USER_ROLES.APPROVER;
  const enabled = queryParams.enabled !== false && !!periodId && !isApprover;
  const { onSuccess, onError, refetchInterval, statisticsJobPollInterval, ...restQueryParams } =
    queryParams;
  const startRefetchInterval = data => {
    if (jobId) return false;
    if (typeof refetchInterval === 'function') return refetchInterval(data);
    return refetchInterval || false;
  };
  const jobRefetchInterval = data => {
    if (!data || data.status === 'queued' || data.status === 'running') {
      if (typeof statisticsJobPollInterval === 'function')
        return statisticsJobPollInterval(data) || 1000;
      if (typeof statisticsJobPollInterval === 'number') return statisticsJobPollInterval;
      if (typeof refetchInterval === 'function') return refetchInterval(data) || 1000;
      if (typeof refetchInterval === 'number') return refetchInterval;
      return 1000;
    }
    return false;
  };

  useEffect(() => {
    setJobId(null);
    setCompletedData(undefined);
    setJobError(null);
  }, [periodId, paramsKey, type]);

  const startQuery = useQuery(
    [...queryKey, 'statistics-job', type, paramsKey],
    () => serviceFundMapping.createStatisticsJob({ type, period_id: periodId, params }),
    {
      ...restQueryParams,
      enabled,
      refetchInterval: startRefetchInterval,
      onSuccess: data => {
        setJobError(null);
        setJobId(data?.job_id);
      },
      onError
    }
  );

  const jobQuery = useQuery(
    [...queryKey, 'statistics-job-status', jobId],
    () => serviceFundMapping.getStatisticsJob(jobId),
    {
      ...restQueryParams,
      enabled: enabled && !!jobId && startQuery.data?.job_id === jobId,
      refetchInterval: jobRefetchInterval,
      onSuccess: data => {
        if (data?.status === 'completed') {
          setCompletedData(data.result);
          setJobError(null);
          onSuccess?.(data.result);
          setJobId(null);
        }
        if (data?.status === 'failed') {
          const error = new Error(data.error || 'Statistics job failed');
          setJobError(error);
          setJobId(null);
          onError?.(error);
        }
      },
      onError
    }
  );

  const isJobRunning = jobQuery.data?.status === 'queued' || jobQuery.data?.status === 'running';
  const refetch = useCallback(() => {
    setJobId(null);
    setJobError(null);
    return startQuery.refetch();
  }, [startQuery.refetch]);

  return {
    ...jobQuery,
    data: completedData,
    error: jobQuery.error || startQuery.error || jobError,
    isError: startQuery.isError || jobQuery.isError || !!jobError,
    isSuccess: completedData !== undefined && !jobError,
    isLoading:
      completedData === undefined && (startQuery.isLoading || jobQuery.isLoading || isJobRunning),
    isFetching: startQuery.isFetching || jobQuery.isFetching || isJobRunning,
    refetch
  };
};

export const useGetFundsByPeriodId = ({ periodId, params = {}, queryParams = {} } = {}) => {
  return useQuery(
    [GET_FUNDS_BY_PERIOD, periodId],
    () => serviceFundMapping.getFundsByPeriodId(periodId, params),
    queryParams
  );
};
export const useGetAutoMatchFunds = ({ sheetId, queryParams = {} } = {}) => {
  return useQuery(
    ['GET_AUTO_MATCH_FUNDS', sheetId],
    () => serviceFundMapping.getAutoMatchFunds(sheetId),
    queryParams
  );
};

export const useGetFundMappings = ({ params = {}, queryParams = {} } = {}) => {
  return useQuery(
    [GET_FUND_MAPPINGS, params],
    () => serviceFundMapping.getFundMappings(params),
    queryParams
  );
};

export const useGetCustomFields = ({ params = {}, queryParams = {} } = {}) => {
  return useQuery(
    [GET_CUSTOM_FIELDS, params],
    () => serviceFundMapping.getCustomFields(params),
    queryParams
  );
};

export const useGetExtractionStatsByPeriodId = ({
  periodId,
  params = {},
  queryParams = {}
} = {}) => {
  return useStatisticsJobQuery({
    queryKey: [GET_EXTRACTION_STATS, periodId],
    periodId,
    params,
    type: 'extraction',
    queryParams
  });
};

export const useGetDetailedExtractionStatsByPeriodId = ({
  periodId,
  params = {},
  queryParams = {}
} = {}) => {
  return useStatisticsJobQuery({
    queryKey: [GET_EXTRACTION_STATS, periodId, params],
    periodId,
    params,
    type: 'extraction_detailed',
    queryParams
  });
};

export const useGetDetailedTransformationStatsByPeriodId = ({
  periodId,
  params = {},
  queryParams = {}
} = {}) => {
  return useStatisticsJobQuery({
    queryKey: [GET_TRANSFORMATION_STATS, periodId, params],
    periodId,
    params,
    type: 'transformation_detailed',
    queryParams
  });
};

export const useGetRefreshTransformationStatsByPeriodId = ({
  periodId,
  params = {},
  queryParams = {}
} = {}) => {
  return useQuery(
    [GET_TRANSFORMATION_STATS, periodId],
    () => serviceFundMapping.getRefreshTransformationStats(periodId, params),
    queryParams
  );
};

export const useGetDetailedOutputStatsByPeriodId = ({
  periodId,
  params = {},
  queryParams = {}
} = {}) => {
  return useStatisticsJobQuery({
    queryKey: [GET_OUTPUT_TABLES_STATS, periodId, params],
    periodId,
    params,
    type: 'output_detailed',
    queryParams
  });
};

export const useValidateDetailedOutputStats = ({ queryParams = {} } = {}) => {
  return useMutation(
    ({ periodId, params = {} }) => serviceFundMapping.getDetailedOutputStats(periodId, params),
    queryParams
  );
};

export const useGetFilesByPeriodId = ({ periodId, params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['GET_FILES_BY_PERIOD_ID', periodId],
    () => serviceFundMapping.getFilesByPeriodId(periodId, params),
    queryParams
  );
};

export const useDeleteCustomField = ({ queryParams = {} } = {}) => {
  return useMutation(id => serviceFundMapping.deleteCustomField(id), queryParams);
};

export const useCreateCustomField = ({ queryParams = {} } = {}) => {
  return useMutation(data => serviceFundMapping.createCustomField(data), queryParams);
};

export const useUpdateCustomField = ({ queryParams = {} } = {}) => {
  return useMutation(data => serviceFundMapping.updateCustomField(data), queryParams);
};

export const useCreateFund = ({ queryParams } = {}) => {
  return useMutation(data => serviceFundMapping.createFund(data), queryParams);
};

export const useCreateSourceFund = ({ queryParams } = {}) => {
  return useMutation(data => serviceFundMapping.createSourceFund(data), queryParams);
};

export const useUpdateSourceFund = ({ queryParams } = {}) => {
  return useMutation(data => serviceFundMapping.updateSourceFund(data), queryParams);
};

export const useDeleteSourceFund = ({ queryParams } = {}) => {
  return useMutation(id => serviceFundMapping.deleteSourceFund(id), queryParams);
};

export const useDeleteSheetSourceFunds = ({ queryParams } = {}) => {
  return useMutation(sheetId => serviceFundMapping.deleteAllSourcesFund(sheetId), queryParams);
};

export const useClearSheetSourceFunds = ({ queryParams } = {}) => {
  return useMutation(sheetId => serviceFundMapping.clearAllSourcesFund(sheetId), queryParams);
};

export const useUpdateFund = ({ queryParams } = {}) => {
  return useMutation(data => serviceFundMapping.updateFund(data), queryParams);
};

export const useDeleteFund = () => {
  return useMutation(fundId => serviceFundMapping.deleteFund(fundId));
};

export const useCreateFundMapping = () => {
  return useMutation(serviceFundMapping.createFundMapping);
};

export const useCreateFundMappingList = () => {
  return useMutation(serviceFundMapping.createFundMappingList);
};

export const useUploadFundFiles = () => {
  return useMutation(serviceFundMapping.uploadFundFiles);
};

export const useReUploadFundFile = () => {
  return useMutation(serviceFundMapping.reUploadFundFile);
};

export const useDeletFundFile = () => {
  return useMutation(serviceFundMapping.deleteFundFile);
};

export const useDeletFundMapping = () => {
  return useMutation(serviceFundMapping.deleteFundMapping);
};

export const useCreateFundsBulk = () => {
  return useMutation(serviceFundMapping.createFundsBulk);
};

export const useDeleteFundsBulk = () => {
  return useMutation(serviceFundMapping.deleteFundsBulk);
};

export const useMarkAsPerInputFund = () => {
  return useMutation(serviceFundMapping.markAsPerInputFund);
};

export const useUnmarkAsPerInputFund = () => {
  return useMutation(serviceFundMapping.unmarkAsPerInputFund);
};
export const useSetPasswordToFile = ({ queryParams = {} } = {}) => {
  return useMutation(serviceFundMapping.setPasswordToFile, queryParams);
};
